/**
 * BedtimeChecklistPage
 * CCH Care — Phase 4b
 *
 * Standalone bedtime checklist page accessible from the dashboard CTA banner
 * and from per-resident Bedtime cards on ResidentDetail. One checklist per
 * resident per date. Covers hygiene, safety, comfort, clinical, environment,
 * and wellbeing assessments.
 *
 * Changes (2026-05-23):
 * - Accept ?patient_id= URL param for deep-links from ResidentDetail's
 *   Bedtime card. Auto-selects the matching resident once the residents
 *   list loads.
 * - Comfort Level buttons now render the descriptive label
 *   (Very Uncomfortable…Very Comfortable) instead of the numeric value.
 *   Per tester feedback — numbers carry no meaning, words match the
 *   sibling Emotional Status field.
 * - Resident-list "X items" count now uses the canonical totalItems
 *   constant (23) instead of the backend's items_total field, which
 *   counts touched items rather than the full checklist definition
 *   (would render as 7/7 when only 7 of 23 had been touched).
 * - Fixed 'COLORS.accentAlpha' string-literal in the in-checklist
 *   "✓ Signed" header pill — was rendering with no background.
 *
 * Changes (2026-04-18):
 * - Dark mode: replaced hardcoded `#fff`, `#FDEDEC`, `#f0fbf5` with
 *   COLORS.surface, COLORS.dangerLight, COLORS.accentLight equivalents.
 * - A11y: "Special Requests / Notes" textarea now has a proper <label
 *   htmlFor> pairing (was a <div> sibling).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';

import { formatTime } from '../../utils/formatters';
const BASE = '/adl/bedtime.php';

// ── Checklist definition ─────────────────────────────────────
const CHECKLIST_SECTIONS = [
  {
    key: 'hygiene',
    label: 'Evening Hygiene',
    icon: '🪥',
    items: [
      { code: 'oral_care',         label: 'Oral care performed' },
      { code: 'face_hands',        label: 'Face and hands washed' },
      { code: 'perineal_care',     label: 'Perineal care performed' },
      { code: 'brief_changed',     label: 'Brief / incontinence product changed' },
      { code: 'skin_cream',        label: 'Skin cream applied' },
    ],
  },
  {
    key: 'comfort',
    label: 'Comfort & Positioning',
    icon: '🛏️',
    items: [
      { code: 'repositioned',       label: 'Resident repositioned for sleep' },
      { code: 'pillows_in_place',   label: 'Pillows / positioning devices in place' },
      { code: 'linens_adjusted',    label: 'Blankets / linens adjusted' },
      { code: 'hob_angle',          label: 'Head of bed at appropriate angle' },
      { code: 'heel_protectors',    label: 'Heel protectors applied (if ordered)' },
    ],
  },
  {
    key: 'safety',
    label: 'Safety',
    icon: '🔒',
    items: [
      { code: 'non_slip_socks',     label: 'Non-slip socks on' },
      { code: 'night_light',        label: 'Night light on' },
      { code: 'alarm_active',       label: 'Bed / chair alarm active (if ordered)' },
      { code: 'id_band',            label: 'ID band on and legible' },
    ],
  },
  {
    key: 'environment',
    label: 'Environment',
    icon: '🌙',
    items: [
      { code: 'room_temp',          label: 'Room temperature comfortable' },
      { code: 'tv_off',             label: 'TV / music off or at resident preference' },
      { code: 'door_positioned',    label: 'Door positioned per resident preference' },
      { code: 'privacy_ensured',    label: 'Privacy ensured' },
    ],
  },
  {
    key: 'clinical',
    label: 'Clinical',
    icon: '🩺',
    items: [
      { code: 'evening_meds',       label: 'Evening medications administered (if applicable)' },
      { code: 'last_fluid',         label: 'Last fluid intake documented' },
      { code: 'last_void',          label: 'Last void / BM documented' },
      { code: 'pain_assessed',      label: 'Pain level assessed' },
      { code: 'equipment_checked',  label: 'Oxygen / equipment checked (if applicable)' },
    ],
  },
];

const COMFORT_LEVELS = [
  { value: 1, emoji: '😣', label: 'Very Uncomfortable' },
  { value: 2, emoji: '😕', label: 'Uncomfortable' },
  { value: 3, emoji: '😐', label: 'Neutral' },
  { value: 4, emoji: '🙂', label: 'Comfortable' },
  { value: 5, emoji: '😊', label: 'Very Comfortable' },
];

const EMOTIONAL_OPTIONS = [
  { value: 'calm',     emoji: '😌', label: 'Calm' },
  { value: 'anxious',  emoji: '😟', label: 'Anxious' },
  { value: 'confused', emoji: '😕', label: 'Confused' },
  { value: 'agitated', emoji: '😤', label: 'Agitated' },
  { value: 'other',    emoji: '💬', label: 'Other' },
];

export default function BedtimeChecklistPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [residents, setResidents]   = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedResident, setSelectedResident] = useState(null);
  const [checklist, setChecklist]   = useState(null);
  const [items, setItems]           = useState({});
  const [wellbeing, setWellbeing]   = useState({ comfort_level: null, emotional_status: null, special_requests: '', fall_risk_checked: false });
  const [saving, setSaving]         = useState(false);
  const [signing, setSigning]       = useState(false);
  const [signer, setSigner]         = useState(null);
  const [error, setError]           = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const isSigned   = checklist?.status === 'completed';
  const totalItems = CHECKLIST_SECTIONS.reduce((sum, s) => sum + s.items.length, 0);
  const checkedCount = Object.values(items).filter(v => v.is_checked).length;

  // ── Load residents list ──────────────────────────────────
  const loadResidents = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await client.get(`${BASE}?action=residents`);
      if (res.data.success) setResidents(res.data.residents || []);
    } catch (err) {
      setError('Failed to load residents');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadResidents(); }, [loadResidents]);

  // Auto-save (debounced 1.5s after user edits)
  useEffect(() => {
    if (!checklist || isSigned) return;
    const timer = setTimeout(() => {
      client.post(`${BASE}?action=save`, {
        bedtime_checklist_id: checklist.bedtime_checklist_id,
        items,
        comfort_level:     wellbeing.comfort_level,
        emotional_status:  wellbeing.emotional_status,
        special_requests:  wellbeing.special_requests,
        fall_risk_checked: wellbeing.fall_risk_checked,
      }).catch(() => {}); // silent — user can still manually save
    }, 1500);
    return () => clearTimeout(timer);
  }, [items, wellbeing, checklist, isSigned]);

  // ── Select resident → load their checklist ───────────────
  const selectResident = useCallback(async (resident) => {
    setSelectedResident(resident);
    setError(null);
    setSaveSuccess(false);
    try {
      const res = await client.get(`${BASE}?action=get&patient_id=${resident.patient_id}`);
      if (res.data.success) {
        const { checklist: cl, items: it, signer: sg } = res.data;
        setChecklist(cl);
        setSigner(sg);
        // Initialize items state
        const itemState = {};
        Object.keys(it).forEach(code => {
          itemState[code] = { is_checked: !!it[code].is_checked, notes: it[code].notes || '' };
        });
        setItems(itemState);
        setWellbeing({
          comfort_level:    cl.comfort_level    ? parseInt(cl.comfort_level)  : null,
          emotional_status: cl.emotional_status || null,
          special_requests: cl.special_requests || '',
          fall_risk_checked: !!cl.fall_risk_checked_at,
        });
      }
    } catch (err) {
      setError('Failed to load checklist');
    }
  }, []);

  // Auto-select a resident if a patient_id param is present in the URL.
  // Used for deep-links from ResidentDetail's Bedtime card. Placed after
  // selectResident is declared to avoid the temporal-dead-zone error on
  // the dependency reference.
  useEffect(() => {
    const preselectPatientId = searchParams.get('patient_id');
    if (!preselectPatientId || selectedResident || residents.length === 0) return;
    const target = residents.find(r => String(r.patient_id) === String(preselectPatientId));
    if (target) selectResident(target);
  }, [searchParams, residents, selectedResident, selectResident]);

  // ── Toggle item ──────────────────────────────────────────
  function toggleItem(code) {
    if (checklist?.status === 'completed') return;
    setItems(prev => ({
      ...prev,
      [code]: { ...prev[code], is_checked: !prev[code]?.is_checked, notes: prev[code]?.notes || '' },
    }));
  }

  // ── Save ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!checklist) return;
    setSaving(true);
    setError(null);
    try {
      const res = await client.post(`${BASE}?action=save`, {
        bedtime_checklist_id: checklist.bedtime_checklist_id,
        items,
        comfort_level:    wellbeing.comfort_level,
        emotional_status: wellbeing.emotional_status,
        special_requests: wellbeing.special_requests,
        fall_risk_checked: wellbeing.fall_risk_checked,
      });
      if (res.data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        loadResidents();
      } else {
        setError(res.data.error || 'Save failed');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Sign ─────────────────────────────────────────────────
  const handleSign = async () => {
    if (!checklist) return;
    setSigning(true);
    setError(null);
    try {
      // Save first
      await client.post(`${BASE}?action=save`, {
        bedtime_checklist_id: checklist.bedtime_checklist_id,
        items,
        comfort_level:    wellbeing.comfort_level,
        emotional_status: wellbeing.emotional_status,
        special_requests: wellbeing.special_requests,
        fall_risk_checked: wellbeing.fall_risk_checked,
      });
      // Then sign
      const res = await client.post(`${BASE}?action=sign`, {
        bedtime_checklist_id: checklist.bedtime_checklist_id,
      });
      if (res.data.success) {
        setChecklist(prev => ({ ...prev, status: 'completed', signed_at: res.data.signed_at }));
        loadResidents();
        // Re-fetch to get signer name
        selectResident(selectedResident);
      } else {
        setError(res.data.error || 'Sign failed');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Sign failed');
    } finally {
      setSigning(false);
    }
  };

  // ── Resident list view ───────────────────────────────────
  if (!selectedResident) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <div style={{ padding: '12px 16px', background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/dashboard/more')} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.primary, padding: '4px 8px' }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>Bedtime Checklist</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>{today}</div>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {loadingList ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading residents...</div>
          ) : residents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🛏️</div>
              <div>No residents found.</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12 }}>
                Select a resident to complete their bedtime checklist.
              </div>
              {residents.map(r => {
                const done = r.checklist_status === 'completed';
                const started = !!r.bedtime_checklist_id;
                return (
                  <div
                    key={r.patient_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectResident(r)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') selectResident(r); }}
                    style={{
                      borderRadius: 10, padding: '14px 16px', marginBottom: 8,
                      border: `1px solid ${done ? COLORS.accentAlpha : COLORS.border}`,
                      background: done ? COLORS.accentLight : COLORS.surface,
                      display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: done ? COLORS.accentLight : COLORS.primaryLight,
                      color: done ? COLORS.accent : COLORS.primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700,
                    }}>
                      {r.first_name[0]}{r.last_name[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                        {r.first_name} {r.last_name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                        Rm {r.room_number}{r.bed_label ? ` · ${r.bed_label}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {done ? (
                        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent }}>✓ Complete</span>
                      ) : started ? (
                        <span style={{ fontSize: 12, color: COLORS.warning, fontWeight: 600 }}>
                          {r.items_checked}/{totalItems} items
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: COLORS.textMuted }}>Not started</span>
                      )}
                    </div>
                    <span style={{ color: COLORS.textMuted, fontSize: 16 }}>›</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Checklist view ───────────────────────────────────────
  return (
    <div style={{ paddingBottom: 160 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={() => setSelectedResident(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedResident.first_name} {selectedResident.last_name}</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>Bedtime Checklist · {today}</div>
        </div>
        {isSigned ? (
          <span style={{ fontSize: 12, fontWeight: 700, background: COLORS.accentAlpha, color: '#fff', padding: '3px 10px', borderRadius: 20 }}>✓ Signed</span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '3px 10px', borderRadius: 20 }}>
            {checkedCount}/{totalItems}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: COLORS.border }}>
        <div style={{ height: '100%', background: isSigned ? COLORS.accent : COLORS.primary, width: `${Math.round((checkedCount / totalItems) * 100)}%`, transition: 'width 0.3s' }} />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Signed banner */}
        {isSigned && signer && (
          <div style={{ background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`, borderRadius: 10, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>Checklist Signed</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                By {signer.first_name} {signer.last_name} ({signer.role}) · {formatTime(checklist.signed_at)}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, fontSize: 13, color: COLORS.danger, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
            {error}
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer' }}>✕</button>
          </div>
        )}

        {saveSuccess && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`, fontSize: 13, color: COLORS.accent, marginBottom: 12 }}>
            ✓ Saved successfully
          </div>
        )}

        {/* Checklist sections */}
        {CHECKLIST_SECTIONS.map(section => (
          <div key={section.key} style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>{section.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{section.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: COLORS.textMuted }}>
                {section.items.filter(i => items[i.code]?.is_checked).length}/{section.items.length}
              </span>
            </div>
            <div style={{ padding: '8px 0' }}>
              {section.items.map(item => {
                const checked = !!items[item.code]?.is_checked;
                return (
                  <div
                    key={item.code}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleItem(item.code)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleItem(item.code); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', cursor: isSigned ? 'default' : 'pointer',
                      background: checked ? 'rgba(46,204,113,0.05)' : 'transparent',
                      borderBottom: `1px solid ${COLORS.bg}`,
                    }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      background: checked ? COLORS.accent : COLORS.surface,
                      border: `2px solid ${checked ? COLORS.accent : COLORS.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, color: '#fff', fontWeight: 700, transition: 'all 0.15s',
                    }}>
                      {checked ? '✓' : ''}
                    </div>
                    <span style={{ fontSize: 13, color: checked ? COLORS.text : COLORS.textMuted, fontWeight: checked ? 600 : 400, flex: 1 }}>
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Fall risk special check */}
        <div style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${wellbeing.fall_risk_checked ? COLORS.primary : COLORS.border}`, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Fall Risk</span>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => !isSigned && setWellbeing(w => ({ ...w, fall_risk_checked: !w.fall_risk_checked }))}
            onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isSigned) setWellbeing(w => ({ ...w, fall_risk_checked: !w.fall_risk_checked })); }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: isSigned ? 'default' : 'pointer' }}
          >
            <div style={{
              width: 24, height: 24, borderRadius: 6, flexShrink: 0,
              background: wellbeing.fall_risk_checked ? COLORS.primary : COLORS.surface,
              border: `2px solid ${wellbeing.fall_risk_checked ? COLORS.primary : COLORS.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: '#fff', fontWeight: 700,
            }}>
              {wellbeing.fall_risk_checked ? '✓' : ''}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: wellbeing.fall_risk_checked ? 600 : 400, color: wellbeing.fall_risk_checked ? COLORS.text : COLORS.textMuted }}>
                Fall risk status confirmed and documented
              </div>
              {checklist?.fall_risk_checked_at && (
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                  Last checked: {formatTime(checklist.fall_risk_checked_at)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Wellbeing section */}
        <div style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>💛</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Resident Wellbeing</span>
            <span style={{ marginLeft: 4, fontSize: 11, color: COLORS.textMuted, fontWeight: 400 }}>(optional but encouraged)</span>
          </div>
          <div style={{ padding: '14px 16px' }}>
            {/* Comfort level */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>Comfort Level</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {COMFORT_LEVELS.map(cl => (
                  <button
                    key={cl.value}
                    onClick={() => !isSigned && setWellbeing(w => ({ ...w, comfort_level: w.comfort_level === cl.value ? null : cl.value }))}
                    disabled={isSigned}
                    title={cl.label}
                    style={{
                      flex: 1, padding: '8px 4px', borderRadius: 8, border: '1px solid',
                      borderColor: wellbeing.comfort_level === cl.value ? COLORS.primary : COLORS.border,
                      background: wellbeing.comfort_level === cl.value ? COLORS.primaryLight : COLORS.surface,
                      cursor: isSigned ? 'default' : 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{cl.emoji}</span>
                    <span style={{
                      fontSize: 10,
                      lineHeight: 1.2,
                      textAlign: 'center',
                      color: wellbeing.comfort_level === cl.value ? COLORS.primary : COLORS.textMuted,
                      fontWeight: 600,
                    }}>{cl.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Emotional status */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>Emotional Status</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {EMOTIONAL_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => !isSigned && setWellbeing(w => ({ ...w, emotional_status: w.emotional_status === opt.value ? null : opt.value }))}
                    disabled={isSigned}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid',
                      borderColor: wellbeing.emotional_status === opt.value ? COLORS.primary : COLORS.border,
                      background: wellbeing.emotional_status === opt.value ? COLORS.primaryLight : COLORS.surface,
                      cursor: isSigned ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 600,
                      color: wellbeing.emotional_status === opt.value ? COLORS.primary : COLORS.textMuted,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    {opt.emoji} {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Special requests */}
            <div>
              <label htmlFor="bedtime-special-requests" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6, display: 'block' }}>
                Special Requests / Notes
              </label>
              <textarea
                id="bedtime-special-requests"
                value={wellbeing.special_requests}
                onChange={e => !isSigned && setWellbeing(w => ({ ...w, special_requests: e.target.value }))}
                disabled={isSigned}
                placeholder="Any special requests or observations..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: `1px solid ${COLORS.border}`, fontSize: 13, resize: 'none',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                  background: isSigned ? COLORS.bg : COLORS.surface,
                  color: COLORS.text,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer action bar */}
      {!isSigned && (
        <div style={{
          position: 'fixed', bottom: 60, left: 0, right: 0,
          background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
          padding: '12px 16px', display: 'flex', gap: 10,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        }}>
          <button
            onClick={handleSave}
            disabled={saving || signing}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 10,
              border: `1px solid ${COLORS.primary}`, background: COLORS.surface,
              color: COLORS.primary, fontSize: 14, fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Progress'}
          </button>
          <button
            onClick={handleSign}
            disabled={saving || signing}
            style={{
              flex: 2, padding: '12px 0', borderRadius: 10,
              border: 'none', background: COLORS.primary,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: signing ? 'wait' : 'pointer', opacity: signing ? 0.7 : 1,
            }}
          >
            {signing ? 'Signing…' : `Sign & Complete 🌙`}
          </button>
        </div>
      )}
    </div>
  );
}